import client from './client';

/**
 * API client for the super-admin-only Approver Delegations tab (Admin Management,
 * Approval Access WP9c). Consumes the WP3 endpoints:
 *   GET   /admin/approverDelegations?delegateAdminEmployeeID=&includeInactive=
 *   POST  /admin/approverDelegations
 *   PATCH /admin/approverDelegations/:id/active   { active: boolean }
 * There is NO DELETE — removal is a soft revoke via PATCH (D25).
 *
 * Shapes mirror the API repo exactly (src/dbInterfaces/EmployeeApproverDelegation.ts):
 * dates are 'YYYY-MM-DD' strings (or null = permanent). The list projection joins the
 * delegate admin's name + role and the target's name for display.
 */

export const DELEGATION_TYPE_SUPERVISOR_TREE = 'SUPERVISOR_TREE';
export const DELEGATION_TYPE_INDIVIDUAL = 'INDIVIDUAL';
export type DelegationType = typeof DELEGATION_TYPE_SUPERVISOR_TREE | typeof DELEGATION_TYPE_INDIVIDUAL;

/** A list/GET row from GET /admin/approverDelegations (EmployeeApproverDelegationView). */
export interface DelegationView {
  employeeApproverDelegationID: number;
  delegateAdminEmployeeID: number;
  delegatedSupervisorEmployeeID: number | null;
  delegatedEmployeeID: number | null;
  delegationType: string; // 'SUPERVISOR_TREE' | 'INDIVIDUAL'
  startDate: string | null; // 'YYYY-MM-DD' or null
  endDate: string | null; // 'YYYY-MM-DD' or null
  active: boolean;
  assignedByAdminEmployeeID: number | null;
  assignedAt: string | null;
  revokedByAdminEmployeeID: number | null;
  revokedAt: string | null;
  delegateAdminName: string | null;
  delegateAdminRoleName: string | null;
  /** Name of the delegated supervisor (SUPERVISOR_TREE) or employee (INDIVIDUAL). */
  targetName: string | null;
}

/** Body for POST /admin/approverDelegations. Exactly one target must match the type. */
export interface CreateDelegationInput {
  delegateAdminEmployeeID: number;
  delegationType: DelegationType;
  delegatedSupervisorEmployeeID?: number | null;
  delegatedEmployeeID?: number | null;
  startDate?: string | null; // 'YYYY-MM-DD'
  endDate?: string | null; // 'YYYY-MM-DD'
}

/**
 * GET /admin/approverDelegations. Passing `includeInactive: true` returns revoked
 * rows too (needed for the "All statuses" filter + audit history); optionally scope
 * to a single delegate.
 */
export const getApproverDelegations = async (opts?: {
  delegateAdminEmployeeID?: number;
  includeInactive?: boolean;
}): Promise<DelegationView[]> => {
  const params: Record<string, unknown> = {};
  if (opts?.delegateAdminEmployeeID != null) params.delegateAdminEmployeeID = opts.delegateAdminEmployeeID;
  if (opts?.includeInactive) params.includeInactive = true;
  const response = await client.get('/admin/approverDelegations', { params });
  return (response.data ?? []) as DelegationView[];
};

/** POST /admin/approverDelegations — create one grant. Returns the created row (201). */
export const createApproverDelegation = async (
  input: CreateDelegationInput,
): Promise<DelegationView> => {
  const response = await client.post('/admin/approverDelegations', input);
  return response.data as DelegationView;
};

/**
 * PATCH /admin/approverDelegations/:id/active — soft revoke (active=false) or
 * restore (active=true). Restore may 400 on a duplicate active grant.
 */
export const setApproverDelegationActive = async (
  employeeApproverDelegationID: number,
  active: boolean,
): Promise<void> => {
  await client.patch(`/admin/approverDelegations/${employeeApproverDelegationID}/active`, { active });
};

/**
 * GET /admin/approverDelegations/subtreeSize/:supervisorEmployeeID (WP4c) — the
 * number of ACTIVE employees recursively under the supervisor, excluding the
 * supervisor themselves. Drives the tree-grant preview line and row sub-lines.
 */
export const getDelegationSubtreeSize = async (
  supervisorEmployeeID: number,
): Promise<number> => {
  const response = await client.get(`/admin/approverDelegations/subtreeSize/${supervisorEmployeeID}`);
  return Number(response.data?.count ?? 0);
};
